import { Typography } from '@mui/material'
import { MaybePromise } from '@reduxjs/toolkit/dist/query/tsHelpers'
import { FC, FormEvent, useEffect, useRef } from 'react'

import { getFinixConfig } from '../../config/configs'

interface Props {
  formId: string
  submitCallback: (
    err: any,
    res: any,
    useLevelOneProcessing: boolean
  ) => MaybePromise<void>
}

interface FieldDefinition {
  idPrefix: string // the substring before "-field-wrapper"
  type: string
  placeholder: string
  validation?: 'required' | 'cardExpiry' | 'cardNumber' | 'cardCVC'
  autoComplete?:
    | 'cc-number'
    | 'cc-name'
    | 'cc-exp'
    | 'cc-csc'
    | 'address-line1'
    | 'address-line2'
    | 'address-level1'
    | 'postal-code'
    | 'address-level2'
}

const FIELD_DEFINITIONS: FieldDefinition[] = [
  {
    idPrefix: 'number',
    type: 'number',
    placeholder: '4111 1111 1111 1111',
    validation: 'required',
    autoComplete: 'cc-number'
  },
  {
    idPrefix: 'expiration-date',
    type: 'expiration_date',
    placeholder: '02/2022',
    validation: 'required',
    autoComplete: 'cc-exp'
  },
  {
    idPrefix: 'security-code',
    type: 'security_code',
    placeholder: 'CVV',
    validation: 'required',
    autoComplete: 'cc-csc'
  },
  {
    idPrefix: 'name',
    type: 'name',
    placeholder: 'John Smith',
    validation: 'required',
    autoComplete: 'cc-name'
  },
  {
    idPrefix: 'addr-line-1',
    type: 'address.line1',
    placeholder: '631 Howard St',
    autoComplete: 'address-line1'
  },
  {
    idPrefix: 'addr-line-2',
    type: 'address.line2',
    placeholder: '#100',
    autoComplete: 'address-line2'
  },
  {
    idPrefix: 'city',
    type: 'address.city',
    placeholder: 'San Francisco',
    autoComplete: 'address-level2'
  },
  {
    idPrefix: 'region',
    type: 'address.region',
    placeholder: 'CA',
    autoComplete: 'address-level1'
  },
  {
    idPrefix: 'postal-code',
    type: 'address.postal_code',
    placeholder: '94105',
    validation: 'required',
    autoComplete: 'postal-code'
  }
]

const AddCardForm: FC<Props> = ({ formId, submitCallback }) => {
  const formSubmitRef = useRef<
    | ((
        env: string,
        appId: string,
        callback: (err: any, res: any) => MaybePromise<void>
      ) => void)
    | null
  >(null)

  useEffect(() => {
    const form = window.PaymentForm.card(() => {})

    const initField: (def: FieldDefinition) => void = (def) => {
      const fieldInput = form.field(def.type, {
        validation: def.validation,
        placeholder: { text: def.placeholder, hideOnFocus: true },
        autoComplete: def.autoComplete,
        styles: {
          default: {
            color: 'black'
          },
          success: {
            color: '#5cb85c'
          },
          error: {
            color: '#d9534f'
          }
        }
      })

      document
        .getElementById(`${def.idPrefix}-field-wrapper`)!
        .appendChild(fieldInput)
    }

    FIELD_DEFINITIONS.forEach((def) => initField(def))
    formSubmitRef.current = form.submit.bind(form)
  }, [])

  const finixSubmitCallback = async function (err: any, res: any) {
    submitCallback(err, res, false)
  }

  const onSubmit: (event: FormEvent) => void = async (event) => {
    event.preventDefault()
    formSubmitRef.current &&
      formSubmitRef.current(
        getFinixConfig().finixEnvironment,
        getFinixConfig().applicationID,
        finixSubmitCallback
      )
  }

  return (
    <form id={formId} onSubmit={onSubmit}>
      <Typography>Name</Typography>
      <div className="fake-field">
        <div className="field-wrapper" id="name-field-wrapper"></div>
      </div>
      <Typography>Address</Typography>
      <div className="fake-field">
        <div className="field-wrapper" id="addr-line-1-field-wrapper"></div>
        <div className="field-wrapper" id="addr-line-2-field-wrapper"></div>
      </div>
      <div className="fake-field">
        <div className="field-wrapper" id="city-field-wrapper"></div>
        <div className="field-wrapper" id="region-field-wrapper"></div>
      </div>
      <div className="fake-field">
        <div className="field-wrapper" id="postal-code-field-wrapper"></div>
      </div>
      <Typography>Card</Typography>
      <div className="fake-field">
        <div className="field-wrapper" id="number-field-wrapper"></div>
      </div>
      <div className="fake-field">
        <div className="field-wrapper" id="expiration-date-field-wrapper"></div>
        <div className="field-wrapper" id="security-code-field-wrapper"></div>
      </div>
    </form>
  )
}

export default AddCardForm
